import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import BrandIcon from '../../components/BrandIcon';

const STATUS_LABEL = {
  pending_payment: { label: 'Aguard. pagamento', color: 'bg-yellow-100 text-yellow-700' },
  waiting_delivery: { label: 'Buscando entregador', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceito', color: 'bg-indigo-100 text-indigo-700' },
  in_transit: { label: 'A caminho', color: 'bg-orange-100 text-orange-700' },
  delivered: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};
const PAYMENT_LABEL = {
  pix: 'PIX',
  debit: 'Débito',
  card: 'Crédito',
};

export default function CustomerHistory() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);
  const [blockedIds, setBlockedIds] = useState([]);
  const [tipLoadingId, setTipLoadingId] = useState(null);
  const [blockReasonByOrder, setBlockReasonByOrder] = useState({});
  const [commentByOrder, setCommentByOrder] = useState({});

  useEffect(() => {
    api.get('/orders/my')
      .then(r => setOrders(r.data))
      .finally(() => setLoading(false));
    api.get('/customer/loyalty').then(r => setLoyalty(r.data)).catch(() => {});
    api.get('/customer/blocked-deliveries').then(r => setBlockedIds(r.data.map(item => item.id))).catch(() => {});
  }, []);

  const rateDelivery = async (orderId, rating) => {
    try {
      const comment = commentByOrder[orderId] || '';
      await api.post(`/orders/${orderId}/rate`, { rating, comment });
      setOrders(prev => prev.map(order => order.id === orderId ? { ...order, customerRating: rating, customerComment: comment } : order));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao avaliar');
    }
  };

  const toggleBlockDelivery = async (deliveryId, orderId) => {
    try {
      if (blockedIds.includes(deliveryId)) {
        await api.delete(`/customer/blocked-deliveries/${deliveryId}`);
        setBlockedIds(prev => prev.filter(id => id !== deliveryId));
      } else {
        await api.post(`/customer/blocked-deliveries/${deliveryId}`, { reason: blockReasonByOrder[orderId] || '' });
        setBlockedIds(prev => [...prev, deliveryId]);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar bloqueio');
    }
  };

  const addTip = async (orderId, amount) => {
    try {
      setTipLoadingId(orderId);
      const response = await api.post(`/orders/${orderId}/tip`, { amount });
      setOrders(prev => prev.map(order => (
        order.id === orderId ? { ...order, tipAmount: response.data.tipAmount } : order
      )));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao enviar gorjeta');
    } finally {
      setTipLoadingId(null);
    }
  };

  return (
    <div className="screen bg-gray-50 flex flex-col">
      <div className="bg-[#1A1A2E] px-6 pt-12 pb-6 text-white">
        <button onClick={() => nav('/cliente/home')} className="text-2xl mb-4 block">←</button>
        <h1 className="text-2xl font-black">Meus Pedidos</h1>
      </div>

      <div className="flex-1 px-4 py-4 overflow-y-auto">
        {loyalty && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-4 mb-4 border border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-orange-500 uppercase tracking-wide">Fidelidade ativa</div>
                <div className="font-black text-gray-800">Cashback: R$ {(loyalty.cashback || 0).toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-1">Pontos: {loyalty.ordersCount || 0} · faltam {loyalty.nextFreeAt || 10} pedido(s) para ganhar R$ {(loyalty.discount || 5).toFixed(2)}</div>
              </div>
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-sm">🎁</div>
            </div>
          </div>
        )}
        {loading && (
          <div className="flex justify-center py-12"><img src="/logo.png" alt="logo" className="w-16 h-16 object-contain animate-bounce" /></div>
        )}

        {!loading && orders.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="text-6xl mb-4">📦</div>
            <p className="font-semibold">Nenhum pedido ainda</p>
            <button onClick={() => nav('/cliente/home')} className="mt-4 text-primary font-bold text-sm">Fazer primeiro pedido →</button>
          </div>
        )}

        <div className="space-y-3">
          {orders.map(o => {
            const st = STATUS_LABEL[o.status] || { label: o.status, color: 'bg-gray-100 text-gray-600' };
            const canTrack = ['accepted', 'in_transit'].includes(o.status);
            return (
              <div key={o.id} className="bg-white rounded-2xl p-4 shadow-sm fade-in">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BrandIcon brandId={o.brandId} size={38} />
                    <div>
                      <div className="font-bold text-gray-800 text-sm">{o.brandName} — {o.sizeLabel}</div>
                      <div className="text-xs text-gray-400">{o.quantity}x • {new Date(o.createdAt).toLocaleDateString('pt-BR')}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span>
                </div>
                <div className="text-xs text-gray-500 mb-3">📍 {o.address}</div>
                <div className="text-xs text-gray-500 mb-3">
                  💳 Pagamento: <span className="font-semibold text-gray-700">{PAYMENT_LABEL[o.paymentMethod] || o.paymentMethod || 'Não informado'}</span>
                </div>
                {o.deliveryPerson?.id && (
                  <div className="mb-3 bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-bold text-sm text-gray-800">{o.deliveryPerson.name}</div>
                        <div className="text-xs text-gray-500">{o.deliveryPerson.vehicle} • ⭐ {o.deliveryPerson.rating?.toFixed?.(1) || o.deliveryPerson.rating}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <button
                          onClick={() => toggleBlockDelivery(o.deliveryPerson.id, o.id)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full ${blockedIds.includes(o.deliveryPerson.id) ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}
                        >
                          {blockedIds.includes(o.deliveryPerson.id) ? 'Desbloquear' : 'Bloquear'}
                        </button>
                        {!blockedIds.includes(o.deliveryPerson.id) && (
                          <input
                            type="text"
                            value={blockReasonByOrder[o.id] || ''}
                            onChange={e => setBlockReasonByOrder(prev => ({ ...prev, [o.id]: e.target.value }))}
                            placeholder="Motivo do bloqueio"
                            className="w-40 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                          />
                        )}
                      </div>
                    </div>
                    {o.status === 'delivered' && (
                      <>
                        <div className="mt-3 flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              onClick={() => rateDelivery(o.id, star)}
                              className={`text-xl ${star <= (o.customerRating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                            >
                              ★
                            </button>
                          ))}
                          <span className="text-xs text-gray-500 ml-2">Avalie o entregador</span>
                        </div>
                        <textarea
                          rows={2}
                          value={commentByOrder[o.id] || o.customerComment || ''}
                          onChange={e => setCommentByOrder(prev => ({ ...prev, [o.id]: e.target.value }))}
                          placeholder="Comentário sobre a entrega"
                          className="mt-3 w-full text-xs border border-gray-200 rounded-xl px-3 py-2 resize-none"
                        />
                        <div className="mt-3">
                          <div className="text-xs text-gray-500 mb-2">Gorjeta para o entregador</div>
                          <div className="flex flex-wrap gap-2">
                            {[2, 5, 10].map(value => (
                              <button
                                key={value}
                                onClick={() => addTip(o.id, value)}
                                disabled={tipLoadingId === o.id}
                                className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 disabled:opacity-50"
                              >
                                + R$ {value.toFixed(2)}
                              </button>
                            ))}
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                step="0.01"
                                placeholder="Outro valor"
                                className="w-24 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                                value={blockReasonByOrder[`tip-${o.id}`] || ''}
                                onChange={e => setBlockReasonByOrder(prev => ({ ...prev, [`tip-${o.id}`]: e.target.value }))}
                              />
                              <button
                                onClick={() => addTip(o.id, Number(blockReasonByOrder[`tip-${o.id}`] || 0))}
                                disabled={tipLoadingId === o.id}
                                className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white disabled:opacity-50"
                              >
                                Enviar
                              </button>
                            </div>
                            {!!o.tipAmount && (
                              <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700">
                                Gorjeta enviada: R$ {o.tipAmount.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {(o.cashbackEarned > 0 || o.loyaltyRewardEarned > 0) && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {o.cashbackEarned > 0 && (
                      <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                        + R$ {o.cashbackEarned.toFixed(2)} cashback
                      </span>
                    )}
                    {o.loyaltyRewardEarned > 0 && (
                      <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                        + R$ {o.loyaltyRewardEarned.toFixed(2)} bônus fidelidade
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-primary font-black">R$ {o.customerPrice?.toFixed(2)}</span>
                  {canTrack && (
                    <button
                      onClick={() => nav(`/cliente/rastreio/${o.id}`)}
                      className="text-xs bg-primary text-white px-3 py-1.5 rounded-full font-bold"
                    >Rastrear</button>
                  )}
                  {o.status === 'waiting_delivery' && (
                    <button
                      onClick={() => nav(`/cliente/aguardando/${o.id}`)}
                      className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-full font-bold"
                    >Ver status</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
